'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FacilityData } from '@/app/_lib/parseFacilities';
import LoadingSpinner from '@/app/_lib/ui/LoadingSpinner';

// Dynamically import the Map component with no SSR
const DynamicMapComponent = dynamic(() => import('./Map'), {
  ssr: false,
  loading: () => <LoadingSpinner message='Loading map...' />
});
interface EnhancedMapWrapperProps {
  facilities: FacilityData[];
  selectedFacility: FacilityData | null;
}
export function EnhancedMapWrapper(t0) {
  const $ = _c(16);
  const {
    facilities,
    selectedFacility
  } = t0;
  const mapRef = useRef(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const infoSectionRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapKey, setMapKey] = useState(0);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (!mapRef.current) {
        return;
      }
      const timer = setTimeout(() => {
        if (!mapRef.current) {
          return;
        }
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setShouldLoadMap(true);
              observer.disconnect();
            }
          });
        }, {
          rootMargin: "200px 0px",
          threshold: 0
        });
        observer.observe(mapRef.current);
        return () => {
          observer.disconnect();
        };
      }, 100);
      return () => {
        clearTimeout(timer);
      };
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[2] !== mapLoaded) {
    t3 = () => {
      if (mapLoaded && infoSectionRef.current) {
        infoSectionRef.current.classList.add("is-visible");
      }
    };
    t4 = [mapLoaded];
    $[2] = mapLoaded;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== mapLoaded || $[6] !== selectedFacility) {
    t5 = () => {
      if (selectedFacility && mapLoaded) {
        setMapKey(_temp);
      }
    };
    t6 = [selectedFacility, mapLoaded];
    $[5] = mapLoaded;
    $[6] = selectedFacility;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ref => {
      infoSectionRef.current = ref;
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const handleInfoSectionRef = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setMapLoaded(true);
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleMapLoad = t8;
  let t9;
  if ($[11] !== facilities || $[12] !== mapKey || $[13] !== selectedFacility || $[14] !== shouldLoadMap) {
    t9 = <div ref={mapRef} id="map-section" className="relative h-[440px] overflow-hidden rounded-xl bg-gray-50 shadow-lg md:h-[540px] dark:bg-gray-900">{shouldLoadMap ? <DynamicMapComponent key={mapKey} onInfoSectionRef={handleInfoSectionRef} onLoad={handleMapLoad} facilities={facilities} selectedFacility={selectedFacility} /> : <div className="flex h-full items-center justify-center bg-gray-100 dark:bg-gray-800"><LoadingSpinner message="Map will load when visible" /><div className="absolute bottom-4 left-4 text-xs text-gray-500">Scroll to this section to load the map</div></div>}</div>;
    $[11] = facilities;
    $[12] = mapKey;
    $[13] = selectedFacility;
    $[14] = shouldLoadMap;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
function _temp(prev) {
  return prev + 1;
}